import { resolveVuetifyTheme } from '@core/utils/vuetify'
import { themeConfig } from '@themeConfig'

// ─── Sensando Design System — Primary color ─────────────────
export const staticPrimaryColor = '#7C3AED'

// ─── Agent Identity Colors ──────────────────────────────────
// Each AI agent has a primary hue so users instantly know "who is talking."
export const agentColors = {
  centinela:  { primary: '#EF4444', light: '#FCA5A5', dark: '#B91C1C', bg: 'rgba(239,68,68,0.08)' },
  jefe:       { primary: '#3B82F6', light: '#93C5FD', dark: '#1D4ED8', bg: 'rgba(59,130,246,0.08)' },
  supervisor: { primary: '#F59E0B', light: '#FCD34D', dark: '#B45309', bg: 'rgba(245,158,11,0.08)' },
}

// ─── Severity Palette (alert / event classification) ────────
export const severityColors = {
  critical: '#EF4444',
  high:     '#F97316',
  medium:   '#F59E0B',
  low:      '#3B82F6',
  info:     '#8B5CF6',
  ok:       '#22C55E',
}

const theme = {
  defaultTheme: resolveVuetifyTheme(),
  themes: {
    // ─── Light Theme ─────────────────────────────────────────
    light: {
      dark: false,
      colors: {
        'primary': localStorage.getItem(`${themeConfig.app.title}-lightThemePrimaryColor`) || staticPrimaryColor,
        'primary-darken-1': '#6D28D9',
        'secondary': '#1E293B',
        'on-secondary': '#E2E8F0',
        'success': '#10B981',
        'info': '#06B6D4',
        'warning': '#F59E0B',
        'error': '#EF4444',
        'on-primary': '#FFFFFF',
        'on-success': '#FFFFFF',
        'on-warning': '#000000',
        'on-error': '#FFFFFF',
        'on-info': '#000000',
        'background': '#F4F5FA',
        'on-background': '#1E293B',
        'on-surface': '#1E293B',
        'surface-variant': '#E2E8F0',
        'surface-bright': '#FFFFFF',
        'grey-50': '#FAFAFA',
        'grey-100': '#F0F2F8',
        'grey-200': '#EEEEEE',
        'grey-300': '#E0E0E0',
        'grey-400': '#BDBDBD',
        'grey-500': '#9E9E9E',
        'grey-600': '#757575',
        'grey-700': '#616161',
        'grey-800': '#424242',
        'grey-900': '#212121',
        'perfect-scrollbar-thumb': '#DBDADE',
        'skin-bordered-background': '#fff',
        'skin-bordered-surface': '#fff',

        // Agent identity (Vuetify custom colors → use as `color="agent-centinela"`)
        'agent-centinela':  agentColors.centinela.primary,
        'agent-jefe':       agentColors.jefe.primary,
        'agent-supervisor': agentColors.supervisor.primary,

        // Severity tokens
        'severity-critical': severityColors.critical,
        'severity-high':     severityColors.high,
        'severity-medium':   severityColors.medium,
        'severity-low':      severityColors.low,
        'severity-info':     severityColors.info,
        'severity-ok':       severityColors.ok,
      },
      variables: {
        'code-color': '#d400ff',
        'overlay-scrim-background': '#1E293B',
        'overlay-scrim-opacity': 0.5,
        'hover-opacity': 0.08,
        'focus-opacity': 0.12,
        'selected-opacity': 0.12,
        'activated-opacity': 0.15,
        'pressed-opacity': 0.12,
        'dragged-opacity': 0.1,
        'border-color': '124, 58, 237',
        'border-opacity': 0.15,
        'table-header-background': '#F0F2F8',
        'custom-background': '#F4F5FA',

        // Typography
        'body-font-family': "'Inter', -apple-system, sans-serif",
        'font-size-root': '14px',

        // Shadows
        'shadow-key-umbra-opacity': 'rgba(var(--v-theme-on-surface), 0.08)',
        'shadow-key-penumbra-opacity': 'rgba(var(--v-theme-on-surface), 0.12)',
        'shadow-key-ambient-opacity': 'rgba(var(--v-theme-on-surface), 0.04)',
      },
    },

    // ─── Dark Theme (Tactical Command Center) ────────────────
    dark: {
      dark: true,
      colors: {
        'primary': localStorage.getItem(`${themeConfig.app.title}-darkThemePrimaryColor`) || staticPrimaryColor,
        'primary-darken-1': '#6D28D9',
        'secondary': '#1E293B',
        'on-secondary': '#E2E8F0',
        'success': '#10B981',
        'info': '#06B6D4',
        'warning': '#F59E0B',
        'error': '#EF4444',
        'on-primary': '#FFFFFF',
        'on-success': '#FFFFFF',
        'on-warning': '#000000',
        'on-error': '#FFFFFF',
        'on-info': '#000000',

        // Command-center dark palette — low cognitive load under stress
        'background': '#0A0E17',
        'on-background': '#E2E8F0',
        'surface': '#111827',
        'on-surface': '#E2E8F0',
        'surface-variant': '#1E293B',
        'surface-bright': '#1A1F2E',
        'grey-50': '#1E293B',
        'grey-100': '#334155',
        'grey-200': '#475569',
        'grey-300': '#64748B',
        'grey-400': '#94A3B8',
        'grey-500': '#94A3B8',
        'grey-600': '#CBD5E1',
        'grey-700': '#E2E8F0',
        'grey-800': '#F1F5F9',
        'grey-900': '#F8FAFC',
        'perfect-scrollbar-thumb': '#475569',
        'skin-bordered-background': '#111827',
        'skin-bordered-surface': '#111827',

        // Agent identity
        'agent-centinela':  agentColors.centinela.primary,
        'agent-jefe':       agentColors.jefe.primary,
        'agent-supervisor': agentColors.supervisor.primary,

        // Severity tokens
        'severity-critical': severityColors.critical,
        'severity-high':     severityColors.high,
        'severity-medium':   severityColors.medium,
        'severity-low':      severityColors.low,
        'severity-info':     severityColors.info,
        'severity-ok':       severityColors.ok,
      },
      variables: {
        'code-color': '#d400ff',
        'overlay-scrim-background': '#0A0E17',
        'overlay-scrim-opacity': 0.7,
        'hover-opacity': 0.08,
        'focus-opacity': 0.12,
        'selected-opacity': 0.12,
        'activated-opacity': 0.15,
        'pressed-opacity': 0.12,
        'dragged-opacity': 0.12,
        'border-color': '124, 58, 237',
        'border-opacity': 0.2,
        'table-header-background': '#1E293B',
        'custom-background': '#111827',

        // Typography
        'body-font-family': "'Inter', -apple-system, sans-serif",
        'font-size-root': '14px',

        // Elevation — deep blacks for military-grade depth
        'shadow-key-umbra-opacity': 'rgba(0, 0, 0, 0.4)',
        'shadow-key-penumbra-opacity': 'rgba(0, 0, 0, 0.3)',
        'shadow-key-ambient-opacity': 'rgba(0, 0, 0, 0.2)',
      },
    },
  },
}

export default theme
